import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'
import type { SupportChannelType } from '@/types/auth'

export interface Company {
  id: string
  name: string
  slug: string
  logo?: string | null
  active: boolean
  // Certificate fields — preenchidos via /admin/companies/me ou super-admin
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
  // Badges configuradas pra aparecer no certificado (read-only pro admin
  // local — quem edita é o super_admin via /super-admin → Empresas).
  certificateBadges?: Array<{
    order: number
    badge: { id: string; name: string; iconUrl: string | null; tier: string }
  }>
  // Canal de atendimento — alimenta o botão flutuante de suporte.
  supportChannelType?: SupportChannelType | null
  supportChannelUrl?: string | null
  supportChannelLabel?: string | null
}

export function useCompanies(enabled = true) {
  return useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get<Company[]>('/admin/companies')
      return response.data
    },
    enabled,
  })
}

// ──────────────────────────────────────────────────────────────────────────
// /admin/companies/me — admin REGULAR vê e edita SUA empresa (C2 do back).
// Super_admin com cid também consegue, mas tipicamente usa o CRUD de
// /admin/companies/:id pra editar qualquer empresa.

export interface UpdateMyCompanyDTO {
  name?: string
  logo?: string | null
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
  supportChannelType?: SupportChannelType | null
  supportChannelUrl?: string | null
  supportChannelLabel?: string | null
}

export function useMyCompany() {
  return useQuery({
    queryKey: ['my-company'],
    queryFn: async () => {
      const response = await api.get<Company>('/admin/companies/me')
      return response.data
    },
  })
}

export function useUpdateMyCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateMyCompanyDTO) => {
      const response = await api.put<Company>('/admin/companies/me', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-company'] })
      // Catálogo global também muda — super_admin pode ver lá
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })
}
